/**
 * `MiniTranscript` — scrollable per-agent activity feed.
 *
 * Extracted from `AgentsPanel.tsx:563-651` (alpha.48) as the C3
 * foundation slice (Issue #686). The same component is consumed by:
 *
 *   - C3 panel rows — `AgentRowCard` / `MainAgentRowCard` mount it
 *     below the row body when the row is expanded.
 *   - C4 banner drawer — the verbose-mode drawer (`ActivityBanner`)
 *     will mount the same component, pointed at whichever agentId
 *     the drawer's dropdown is on.
 *
 * Prop shape is preserved verbatim from the inline version so the
 * extraction is purely structural — no behaviour change, alpha.48
 * visual parity:
 *
 *   - `chunks: readonly AgentStreamChunk[]` — the row's
 *     `recentChunks` slice (capped at `MAX_RECENT_CHUNKS_PER_AGENT`).
 *   - `chunksSeenTotal: number` — monotonic counter of chunks ever
 *     appended; used to derive stable per-chunk keys that survive
 *     front-trim shifts when the cap kicks in.
 *
 * Stable per-chunk key derivation: `chunksSeenTotal - chunks.length + i`
 * is a chunk's permanent insertion-order index — survives front-trim
 * when the cap kicks in. Reviewer catch on Phase 2: index keys break
 * the moment trim slides indices, leaving React reconciling chunk N's
 * content into chunk N-1's DOM slot.
 *
 * Empty-state contract: when `chunks` is empty, renders the
 * "No activity yet." copy. This is also the rendered shape during
 * the turn-boundary drift window documented in
 * `docs/c3-agents-panel-design.md` §2 (chat-reducer's `agents` slice
 * clears at `startTurn` before the registry's snapshot clears).
 *
 * `summariseToolInput` + `shorten` live here too, owned by this
 * module. `AgentsPanel.tsx` imports them via `recentActivityHint` so
 * the two surfaces stay in lockstep on how a tool's input is
 * summarised. Lifting to a third "util" module would be premature —
 * the helpers are tightly coupled to the transcript-line render
 * shape and have no other consumer.
 */

import type { AgentStreamChunk } from '@volley/crucible-ipc-types/ipc-channels';

export interface MiniTranscriptProps {
  readonly chunks: readonly AgentStreamChunk[];
  readonly chunksSeenTotal: number;
}

export function MiniTranscript({
  chunks,
  chunksSeenTotal,
}: MiniTranscriptProps): React.JSX.Element {
  if (chunks.length === 0) {
    return (
      <p className="px-3 pb-3 pl-9 text-xs italic text-white/30">
        No activity yet.
      </p>
    );
  }

  // Stable per-chunk key derived from the row's lifetime monotonic
  // counter. `chunksSeenTotal - chunks.length + i` is a chunk's
  // permanent insertion-order index — survives front-trim when the
  // cap kicks in. Reviewer catch on Phase 2: index keys break the
  // moment trim slides indices, leaving React reconciling
  // chunk N's content into chunk N-1's DOM slot.
  const baseSeq = chunksSeenTotal - chunks.length;
  return (
    <div
      className="max-h-64 overflow-auto border-t border-white/5 bg-black/20 px-3 py-2 pl-9 font-mono text-[11px] leading-relaxed text-white/60"
      data-testid="agent-mini-transcript"
    >
      {chunks.map((c, i) => (
        <TranscriptLine key={baseSeq + i} chunk={c} />
      ))}
    </div>
  );
}

export function TranscriptLine({
  chunk,
}: {
  chunk: AgentStreamChunk;
}): React.JSX.Element | null {
  switch (chunk.type) {
    case 'tool_call': {
      const tc = chunk.toolCall;
      if (!tc) return null;
      const summary = summariseToolInput(tc.name, tc.input);
      return (
        <div className="flex gap-1.5">
          <span className="text-cyan-400/80">→</span>
          <span className="text-white/70">{tc.name}</span>
          {summary && <span className="truncate text-white/40">{summary}</span>}
        </div>
      );
    }
    case 'tool_result': {
      const tr = chunk.toolResult;
      if (!tr) return null;
      const truncated = (tr.output ?? '').replace(/\n+/g, ' ').slice(0, 120);
      return (
        <div className="flex gap-1.5">
          <span className={tr.isError ? 'text-red-400/80' : 'text-emerald-400/80'}>
            ←
          </span>
          <span className="truncate text-white/40">
            {tr.isError ? 'error: ' : ''}
            {truncated}
          </span>
        </div>
      );
    }
    case 'text': {
      const text = (chunk.text ?? '').trim();
      if (!text) return null;
      const condensed = text.replace(/\s+/g, ' ').slice(0, 160);
      return <p className="text-white/50">{condensed}</p>;
    }
    case 'warning': {
      const msg = (chunk.error ?? '').slice(0, 160);
      return (
        <div className="flex gap-1.5 text-amber-300/80">
          <span>⚠</span>
          <span className="truncate">{msg}</span>
        </div>
      );
    }
    default:
      // agent_started / agent_stopped / done / interrupted / error
      // chunks aren't included in `recentChunks` (the reducer only
      // appends agentId-tagged passthrough chunks per
      // `applyAgentTracking`), so this branch is unreachable in
      // practice. Keep it exhaustive so a future chunk type addition
      // gets a TypeScript nudge here.
      return null;
  }
}

/**
 * Summarise a tool's input for a single-line hint. Each tool name's
 * "interesting" arg differs (Read uses file_path, Bash uses command,
 * Grep uses pattern). Fall back to a short JSON dump for unknowns.
 *
 * Owned by `MiniTranscript.tsx` (since the transcript-line render is
 * the primary consumer); `AgentsPanel.tsx` re-imports for the
 * collapsed-row `recentActivityHint` helper so the two surfaces stay
 * in lockstep.
 */
export function summariseToolInput(
  toolName: string,
  input: Record<string, unknown>,
): string {
  switch (toolName) {
    case 'Read':
    case 'Write':
    case 'Edit':
      return typeof input.file_path === 'string'
        ? shorten(input.file_path, 60)
        : '';
    case 'Bash':
      return typeof input.command === 'string'
        ? shorten(input.command, 60)
        : '';
    case 'Grep':
      return typeof input.pattern === 'string'
        ? shorten(input.pattern, 60)
        : '';
    case 'Glob':
      return typeof input.pattern === 'string'
        ? shorten(input.pattern, 60)
        : '';
    case 'Agent':
    case 'Task':
      return typeof input.subagent_type === 'string'
        ? `→ ${input.subagent_type}`
        : '';
    case 'AskUserQuestion':
      return '';
    default: {
      const json = JSON.stringify(input);
      return shorten(json, 60);
    }
  }
}

function shorten(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

// Default export so C4's banner-drawer (and any other future
// consumer) can mount the component with either `import MiniTranscript`
// (the literal "single default component" shape called out in C3
// Issue A's acceptance criteria) or `import { MiniTranscript }`
// (the named-export shape current `AgentsPanel.tsx` uses). Same
// component object either way — no duplicate render path, no
// duplicate type surface.
export default MiniTranscript;
